import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  ShoppingCart,
  Calendar,
  X,
  Download,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Check,
  ChevronDown,
} from "lucide-react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";

const statusColors = {
  Approved: "bg-emerald-100 text-emerald-800",
  Delivered: "bg-emerald-100 text-emerald-800",
  "Partially Delivered": "bg-blue-100 text-blue-800",
  "In Transit": "bg-blue-100 text-blue-800",
  "Pending Approval": "bg-amber-100 text-amber-800",
  Cancelled: "bg-red-100 text-red-800",
  Canceled: "bg-red-100 text-red-800",
  Delayed: "bg-red-100 text-red-800",
  Open: "bg-blue-50 text-blue-700 border-blue-200",
  "In Process": "bg-amber-50 text-amber-700 border-amber-200",
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

const POTable = ({ data, suppliers: propSuppliers, onExport, exporting }) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedPlant, setSelectedPlant] = useState("all");
  const [selectedSupplier, setSelectedSupplier] = useState("all");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState("all");
  const [selectedDatePreset, setSelectedDatePreset] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState("50");

  const rawData = data || [];

  // Reset to page 1 whenever any filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    statusFilter,
    selectedPlant,
    selectedSupplier,
    selectedTier,
    selectedDatePreset,
    fromDate,
    toDate,
    pageSize,
  ]);

  // Master supplier list from SAP (1,200+ vendors)
  const masterSuppliers = useMemo(() => {
    if (propSuppliers && propSuppliers.length > 0) return propSuppliers;
    const map = {};
    rawData.forEach((po) => {
      const id = po.supplier_id || po.vendor || "";
      const name = po.vendor_name || po.vendor || id;
      if (id && !map[id]) {
        map[id] = {
          supplier_id: id,
          supplier_name: name,
          display_name: `${id} - ${name}`,
        };
      }
    });
    return Object.values(map).sort((a, b) => (a.supplier_name || "").localeCompare(b.supplier_name || ""));
  }, [propSuppliers, rawData]);

  const filteredSuppliersList = useMemo(() => {
    if (!supplierSearch.trim()) return masterSuppliers.slice(0, 80);
    const q = supplierSearch.toLowerCase();
    return masterSuppliers
      .filter((s) => s.supplier_id.toLowerCase().includes(q) || s.supplier_name.toLowerCase().includes(q))
      .slice(0, 80);
  }, [masterSuppliers, supplierSearch]);

  const selectedSupplierObj = useMemo(() => {
    if (selectedSupplier === "all") return null;
    return masterSuppliers.find((s) => s.supplier_id === selectedSupplier || s.supplier_name === selectedSupplier);
  }, [masterSuppliers, selectedSupplier]);

  // Unique filter values from data with standard options
  const statuses = useMemo(() => {
    const set = new Set(rawData.map((po) => po.status).filter(Boolean));
    set.add("Completed");
    return Array.from(set);
  }, [rawData]);
  const plants = useMemo(() => [...new Set(rawData.map((po) => po.plant || "Plant 1000").filter(Boolean))], [rawData]);
  const categories = useMemo(() => [...new Set(rawData.map((po) => po.category || "General").filter(Boolean))], [rawData]);

  // Rolling Last 6 Calendar Days
  const last6Days = useMemo(() => {
    const dates = [];
    for (let i = 0; i < 6; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      dates.push(d.toISOString().slice(0, 10));
    }
    return dates;
  }, []);

  // Date benchmark strings
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const yesterdayStr = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  }, []);
  const thirtyDaysAgoStr = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  }, []);

  const hasFiltersActive = useMemo(() => {
    return (
      search.trim() !== "" ||
      statusFilter !== "all" ||
      selectedPlant !== "all" ||
      selectedSupplier !== "all" ||
      selectedTier !== "all" ||
      selectedDatePreset !== "all" ||
      Boolean(fromDate) ||
      Boolean(toDate)
    );
  }, [
    search,
    statusFilter,
    selectedPlant,
    selectedSupplier,
    selectedTier,
    selectedDatePreset,
    fromDate,
    toDate,
  ]);

  const handleResetFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setSelectedPlant("all");
    setSelectedSupplier("all");
    setSupplierSearch("");
    setSelectedTier("all");
    setSelectedDatePreset("all");
    setFromDate("");
    setToDate("");
  };

  const handleDatePresetChange = (val) => {
    setSelectedDatePreset(val);
    if (val === "all") {
      setFromDate("");
      setToDate("");
    } else if (val === "today") {
      setFromDate(todayStr);
      setToDate(todayStr);
    } else if (val === "yesterday") {
      setFromDate(yesterdayStr);
      setToDate(yesterdayStr);
    } else if (val === "last6") {
      setFromDate(last6Days[5]);
      setToDate(last6Days[0]);
    } else if (val === "last30") {
      setFromDate(thirtyDaysAgoStr);
      setToDate(todayStr);
    }
  };

  // Master filtered list
  const filtered = useMemo(() => {
    return rawData.filter((po) => {
      // 1. Plant filter
      const matchPlant = selectedPlant === "all" || (po.plant || "Plant 1000") === selectedPlant;

      // 2. Supplier filter
      const vName = po.vendor_name || po.vendor || "";
      const sId = po.supplier_id || "";
      const matchSupplier =
        selectedSupplier === "all" ||
        sId === selectedSupplier ||
        vName === selectedSupplier;

      // 3. Status filter
      const matchStatus = statusFilter === "all" || po.status === statusFilter;

      // 4. Value Tier filter
      const val = Number(po.net_value || 0);
      let matchTier = true;
      if (selectedTier === "high") matchTier = val >= 10_00_000;
      else if (selectedTier === "medium") matchTier = val >= 1_00_000 && val < 10_00_000;
      else if (selectedTier === "low") matchTier = val < 1_00_000;

      // 5. Creation Date filter (From - To range)
      const poDate = (po.creation_date || "").slice(0, 10);
      let matchDate = true;
      if (fromDate && toDate) {
        matchDate = poDate >= fromDate && poDate <= toDate;
      } else if (fromDate) {
        matchDate = poDate >= fromDate;
      } else if (toDate) {
        matchDate = poDate <= toDate;
      }

      // 6. Search query
      const q = search.toLowerCase();
      const matchSearch =
        !search ||
        (po.po_number && po.po_number.toLowerCase().includes(q)) ||
        (po.vendor_name && po.vendor_name.toLowerCase().includes(q)) ||
        (po.vendor && po.vendor.toLowerCase().includes(q)) ||
        (po.supplier_id && po.supplier_id.toLowerCase().includes(q)) ||
        (po.material && po.material.toLowerCase().includes(q)) ||
        (po.material_desc && po.material_desc.toLowerCase().includes(q));

      return matchPlant && matchSupplier && matchStatus && matchTier && matchDate && matchSearch;
    });
  }, [
    rawData,
    search,
    statusFilter,
    selectedPlant,
    selectedSupplier,
    selectedTier,
    selectedDatePreset,
    fromDate,
    toDate,
    todayStr,
    yesterdayStr,
    last6Days,
    thirtyDaysAgoStr,
  ]);

  if (!data || data.length === 0) {
    return <p className="text-center text-muted-foreground py-8">No purchase order data</p>;
  }

  // Pagination calculation
  const numericPageSize = pageSize === "all" ? filtered.length : Number(pageSize);
  const totalPages = Math.max(1, Math.ceil(filtered.length / (numericPageSize || 1)));
  const startIndex = (currentPage - 1) * numericPageSize;
  const endIndex = Math.min(startIndex + numericPageSize, filtered.length);
  const paginatedRows = filtered.slice(startIndex, endIndex);

  return (
    <div className="space-y-4" data-testid="pu-po-container">
      {/* Filtering Toolbar Card */}
      <Card className="card-industrial">
        <CardContent className="p-3.5 space-y-3">
          {/* Row 1: Main Filter Controls */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Search */}
            <div className="relative flex-1 min-w-[170px] max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Search PO, vendor, material..."
                className="pl-8 h-8 text-xs"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="pu-po-search"
              />
            </div>

            {/* Date Preset & From-To Date Range Pickers */}
            <div className="flex items-center gap-1.5 bg-muted/40 p-1 rounded-md border shrink-0">
              <Calendar className="w-3.5 h-3.5 ml-1 text-violet-600 shrink-0" />
              <Select value={selectedDatePreset} onValueChange={handleDatePresetChange}>
                <SelectTrigger className="w-[105px] h-7 text-xs border-0 bg-transparent shadow-none" data-testid="pu-po-date-preset">
                  <SelectValue placeholder="All Dates" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Dates</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="last6">Last 6 Days</SelectItem>
                  <SelectItem value="last30">Last 30 Days</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center gap-1 border-l border-muted-foreground/20 pl-1.5">
                <span className="text-[11px] text-muted-foreground font-medium">From:</span>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setSelectedDatePreset("custom");
                  }}
                  className="w-[115px] h-7 text-xs font-medium border-0 bg-background shadow-xs px-1.5"
                  title="From creation date"
                  data-testid="pu-po-from-date"
                />
                <span className="text-[11px] text-muted-foreground font-medium">To:</span>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setSelectedDatePreset("custom");
                  }}
                  className="w-[115px] h-7 text-xs font-medium border-0 bg-background shadow-xs px-1.5"
                  title="To creation date"
                  data-testid="pu-po-to-date"
                />
              </div>

              {(fromDate || toDate) && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setFromDate("");
                    setToDate("");
                    setSelectedDatePreset("all");
                  }}
                  title="Clear date range"
                  data-testid="pu-po-clear-date"
                >
                  <X className="w-3 h-3" />
                </Button>
              )}
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="pu-po-status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statuses.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Plant Filter */}
            <Select value={selectedPlant} onValueChange={setSelectedPlant}>
              <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="pu-po-plant-filter">
                <SelectValue placeholder="All Plants" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plants</SelectItem>
                {plants.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Searchable Supplier Popover */}
            <Popover open={supplierOpen} onOpenChange={setSupplierOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 min-w-[140px] max-w-[210px] justify-between text-xs font-normal border-input bg-background"
                  data-testid="pu-po-supplier-filter"
                >
                  <span className="truncate">
                    {selectedSupplierObj ? selectedSupplierObj.supplier_name || selectedSupplierObj.display_name : "All Suppliers"}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-2 z-50 shadow-lg border bg-background" align="start">
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search 1,200+ suppliers by ID or name..."
                      className="pl-7 h-7 text-xs"
                      value={supplierSearch}
                      onChange={(e) => setSupplierSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="text-[10px] text-muted-foreground flex justify-between px-1 border-b pb-1">
                    <span>{supplierSearch ? `Matching results` : `Showing top 80 suppliers`}</span>
                    <span>Total: {masterSuppliers.length}</span>
                  </div>
                  <div className="max-h-60 overflow-y-auto space-y-0.5 pr-1 text-xs">
                    <button
                      onClick={() => {
                        setSelectedSupplier("all");
                        setSupplierOpen(false);
                        setSupplierSearch("");
                      }}
                      className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors flex items-center justify-between cursor-pointer ${
                        selectedSupplier === "all" ? "bg-violet-600 text-white font-semibold" : "hover:bg-muted text-foreground"
                      }`}
                    >
                      <span>All Suppliers ({masterSuppliers.length})</span>
                      {selectedSupplier === "all" && <Check className="w-3.5 h-3.5 shrink-0" />}
                    </button>

                    {filteredSuppliersList.map((s) => {
                      const isSelected = selectedSupplier === s.supplier_id || selectedSupplier === s.supplier_name;
                      return (
                        <button
                          key={s.supplier_id}
                          onClick={() => {
                            setSelectedSupplier(s.supplier_id);
                            setSupplierOpen(false);
                          }}
                          className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors flex items-center justify-between cursor-pointer ${
                            isSelected ? "bg-violet-600 text-white font-semibold" : "hover:bg-muted text-foreground"
                          }`}
                          title={s.display_name}
                        >
                          <div className="truncate pr-2">
                            <span className="font-mono text-[10px] opacity-70 block">{s.supplier_id}</span>
                            <span className="truncate block font-medium">{s.supplier_name}</span>
                          </div>
                          {isSelected && <Check className="w-3.5 h-3.5 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Value Tier Filter */}
            <Select value={selectedTier} onValueChange={setSelectedTier}>
              <SelectTrigger className="w-[115px] h-8 text-xs" data-testid="pu-po-tier-filter">
                <SelectValue placeholder="All Tiers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="high">High (&gt; ₹10L)</SelectItem>
                <SelectItem value="medium">Medium (₹1L-₹10L)</SelectItem>
                <SelectItem value="low">Low (&lt; ₹1L)</SelectItem>
              </SelectContent>
            </Select>

            {/* Reset Filters */}
            {hasFiltersActive && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs text-muted-foreground hover:text-foreground px-2"
                onClick={handleResetFilters}
                data-testid="pu-po-reset-btn"
              >
                Reset Filters
              </Button>
            )}
          </div>

          {/* Row 2: Last 6 Days Quick Badges & Export Button */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2.5 border-t border-muted/50 text-xs">
            {/* Left: Last 6 Days Quick Buttons */}
            <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-muted-foreground">
              <span className="font-semibold text-foreground/85 flex items-center gap-1">
                <Calendar className="w-3 h-3 text-violet-600" />
                Last 6 Days:
              </span>
              {last6Days.map((d, idx) => {
                const label = idx === 0 ? `${d} (Today)` : idx === 1 ? `${d} (Yday)` : d;
                const isSelected = fromDate === d && toDate === d;
                return (
                  <button
                    key={d}
                    onClick={() => {
                      if (isSelected) {
                        setFromDate("");
                        setToDate("");
                        setSelectedDatePreset("all");
                      } else {
                        setFromDate(d);
                        setToDate(d);
                        setSelectedDatePreset("custom");
                      }
                    }}
                    className={`px-2.5 py-0.5 rounded transition-all cursor-pointer font-mono text-[10px] ${
                      isSelected
                        ? "bg-violet-600 text-white font-bold shadow-xs scale-105"
                        : "bg-muted/80 hover:bg-violet-100 hover:text-violet-900 border border-muted-foreground/20"
                    }`}
                    title={`Filter orders created on ${d}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Right: Export Button */}
            {onExport && (
              <div className="ml-auto">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs gap-1.5 shadow-xs"
                  onClick={() => onExport("purchase_orders")}
                  disabled={exporting}
                  data-testid="pu-export-po-btn"
                >
                  {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                  Export POs
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Orders Data Table Card */}
      <Card className="card-industrial" data-testid="pu-po-table">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-violet-600" />
              Purchase Orders ({filtered.length})
            </CardTitle>
            {hasFiltersActive && (
              <Badge variant="secondary" className="text-xs font-normal">
                Filtered: {filtered.length} of {rawData.length} lines
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  <th className="text-left p-3">PO #</th>
                  <th className="text-left p-3">Vendor Name</th>
                  <th className="text-left p-3">Material</th>
                  <th className="text-left p-3">Category</th>
                  <th className="text-right p-3">Qty</th>
                  <th className="text-right p-3">Net Value</th>
                  <th className="text-left p-3">Expected</th>
                  <th className="text-center p-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-muted/30">
                {paginatedRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-muted-foreground text-sm">
                      No purchase orders match your selected filters.
                    </td>
                  </tr>
                ) : (
                  paginatedRows.map((po, idx) => (
                    <tr key={idx} className="hover:bg-muted/30 transition-colors" data-testid={`pu-po-row-${idx}`}>
                      <td className="p-3 font-mono text-xs font-medium text-violet-700">{po.po_number}</td>
                      <td className="p-3">
                        <div className="font-medium text-foreground">{po.vendor_name || po.vendor}</div>
                        {po.supplier_id && po.supplier_id !== (po.vendor_name || po.vendor) && (
                          <div className="text-[11px] font-mono text-muted-foreground">
                            {po.supplying_plant ? `Plant ${po.supplying_plant}` : `ID: ${po.supplier_id}`}
                          </div>
                        )}
                      </td>
                      <td className="p-3 font-mono text-xs text-muted-foreground">{po.material}</td>
                      <td className="p-3">
                        <Badge variant="outline" className="text-xs font-normal">
                          {po.category}
                        </Badge>
                      </td>
                      <td className="p-3 text-right">{po.quantity?.toLocaleString()}</td>
                      <td className="p-3 text-right font-semibold text-foreground">
                        {po.net_value?.toLocaleString("en-IN", {
                          style: "currency",
                          currency: "INR",
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td className="p-3 text-xs text-muted-foreground">{po.expected_date || "—"}</td>
                      <td className="p-3 text-center">
                        <Badge className={`text-xs ${statusColors[po.status] || "bg-slate-100 text-slate-800"}`}>
                          {po.status}
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>

        {/* Pagination Footer */}
        {filtered.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-muted/50 text-xs text-muted-foreground">
            {/* Record Count Range */}
            <div>
              Showing <b className="text-foreground">{startIndex + 1}</b> to <b className="text-foreground">{endIndex}</b> of <b className="text-foreground">{filtered.length}</b> purchase orders
            </div>

            {/* Controls: Page Size + Page Navigation */}
            <div className="flex items-center gap-4 flex-wrap">
              {/* Page Size Selector */}
              <div className="flex items-center gap-1.5">
                <span>Rows per page:</span>
                <Select value={pageSize} onValueChange={setPageSize}>
                  <SelectTrigger className="w-[70px] h-7 text-xs" data-testid="pu-po-page-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Page Navigation Buttons */}
              {pageSize !== "all" && totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    title="First page"
                  >
                    <ChevronsLeft className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    title="Previous page"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </Button>

                  <span className="px-2 font-medium text-foreground">
                    Page {currentPage} of {totalPages}
                  </span>

                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    title="Next page"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage >= totalPages}
                    title="Last page"
                  >
                    <ChevronsRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default POTable;
